import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectIsCurrentElementMultiResizing,
    selectIsResizingElement,
    selectAllowChangingAspectRatio,
} from './store/resizingSelector';
import { getNextUndoElementMoveAndUpdateTransactionId } from '../../utils/undoRedo/undoRedoActions';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { ImMNElement, MNElementLocation } from '../../../common/elements/elementModelTypes';
import { getElementId, getElementLocation } from '../../../common/elements/utils/elementPropertyUtils';
import { atomicMoveAndUpdate } from '../actions/elementActions';
import useStickySelector from '../../utils/react/useStickySelector';
import { getCanChangeAspectRatio, getResizeAspectLock } from './resizeUtils';
import { MaintainAspectRatioMode, ResizeChanges, SetElementSizeAndPosition, SetTempElementSize } from './resizeTypes';
import { Point } from '../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import usePrevious from '../../utils/react/usePrevious';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { IMAGE_MIN_HEIGHT_GRID } from '../../../common/images/imageConstants';

export type GroupResizeStateProps = {
    element: ImMNElement;
    elementId: string;
};

export type ElementResizeState = {
    isResizing: boolean;
    tempSize: RectSize | null;
    setTempElementSize: SetTempElementSize;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    isResizingOrAnimating: boolean;
    isMultiResizingOrAnimating: boolean;
    allowAspectChangeForCurrentResize: boolean;
    resizeAspectLock: string;
    onResizeAnimationComplete: () => void;
    minWidth: number;
    minHeight: number;
};

const useGroupResizeState = (
    props: GroupResizeStateProps,
    getMaintainAspectRatioMode = (props: GroupResizeStateProps) => MaintainAspectRatioMode.NEVER,
    minWidth = ELEMENT_DEFAULT_WIDTH,
    minHeight = IMAGE_MIN_HEIGHT_GRID,
): ElementResizeState => {
    const { element, elementId } = props;

    const [tempSize, setTempElementSize] = useState<RectSize | null>(null);

    const isResizing = useSelector(selectIsResizingElement(elementId));
    const prevIsResizing = usePrevious(isResizing);

    const [isResizingOrAnimating, setisResizingOrAnimating] = useState(false);
    if (isResizing && !prevIsResizing && !isResizingOrAnimating) {
        setisResizingOrAnimating(true);
    }
    const onResizeAnimationComplete = () => {
        setisResizingOrAnimating(false);
    };

    const dispatch = useDispatch();
    const getLastMoveUpdateTransactionId = () =>
        dispatch(getNextUndoElementMoveAndUpdateTransactionId()) as unknown as number | undefined;

    const previousTransactionIdRef = React.useRef<number | undefined>();
    previousTransactionIdRef.current = useMemo(
        isResizing ? getLastMoveUpdateTransactionId : () => previousTransactionIdRef.current,
        [isResizing],
    );

    const setElementSizeAndPosition = useCallback(
        (changes: ResizeChanges, position?: Point) => {
            // Use the same transaction ID for all elements resizing in the same group
            const latestTransactionId = getLastMoveUpdateTransactionId();
            const shouldGetNewId = !latestTransactionId || latestTransactionId === previousTransactionIdRef.current;
            const transactionId = shouldGetNewId ? getNewTransactionId() : latestTransactionId;

            const location: MNElementLocation = getElementLocation(element).toJS();
            if (position && location.position) {
                location.position.x = Math.round(position.x);
                location.position.y = Math.round(position.y);
            }

            dispatch(
                atomicMoveAndUpdate({
                    id: getElementId(element),
                    changes,
                    location,
                    transactionId,
                }),
            );
        },
        [element],
    );

    const isAnimating = isResizingOrAnimating && !isResizing;
    const isMultiResizingOrAnimating = useStickySelector(selectIsCurrentElementMultiResizing(elementId), isAnimating);
    const allowAspectChangeForCurrentResize = useStickySelector(selectAllowChangingAspectRatio(elementId), isAnimating);

    const maintainAspectRatioMode = getMaintainAspectRatioMode(props);
    const canChangeAspectRatio = getCanChangeAspectRatio(maintainAspectRatioMode, allowAspectChangeForCurrentResize);
    const resizeAspectLock = getResizeAspectLock(canChangeAspectRatio, isMultiResizingOrAnimating);

    return {
        isResizing,
        tempSize,
        setTempElementSize,
        setElementSizeAndPosition,
        isResizingOrAnimating,
        isMultiResizingOrAnimating,
        allowAspectChangeForCurrentResize,
        resizeAspectLock,
        onResizeAnimationComplete,
        minWidth,
        minHeight,
    };
};

export default useGroupResizeState;
